/**
 * Health Canada eCTD Sequence Descriptions — controlled vocabulary from
 * HC eCTD Guidance Document, Appendix 4 / Table 4.
 *
 * These are the exact phrases that must appear in the SequenceDescription
 * field of an HC eCTD submission. Validation rules HC-M1-08 and HC-M1-09
 * check that the submitted description matches one of these entries.
 *
 * Source: Health Canada "eCTD Guidance Document" (public domain regulatory
 * guidance), Table 4 — Sequence Descriptions.
 *
 * Phase 7.4 — GAP-M-03.
 */
export interface HcSequenceDescription {
  /** Short code used internally for mapping. */
  code: string;
  /** The exact label as it appears in HC Table 4. */
  label: string;
  /** HC submission type codes this description applies to. */
  applicableTypes: string[];
}

export const HC_SEQUENCE_DESCRIPTIONS: ReadonlyArray<HcSequenceDescription> = [
  // New Drug Submissions
  {
    code: 'NDS-ORIG',
    label: 'New Drug Submission (NDS)',
    applicableTypes: ['NDS'],
  },
  {
    code: 'ANDS-ORIG',
    label: 'Abbreviated New Drug Submission (ANDS)',
    applicableTypes: ['ANDS'],
  },
  {
    code: 'SNDS-ORIG',
    label: 'Supplement to a New Drug Submission (SNDS)',
    applicableTypes: ['SNDS'],
  },
  {
    code: 'SANDS-ORIG',
    label: 'Supplement to an Abbreviated New Drug Submission (SANDS)',
    applicableTypes: ['SANDS'],
  },
  // New Drug Submission — Prior Approval Supplements
  {
    code: 'SNDS-PAS',
    label: 'Supplement to a New Drug Submission - Prior Approval Supplement (SNDS-PAS)',
    applicableTypes: ['SNDS'],
  },
  {
    code: 'SANDS-PAS',
    label: 'Supplement to an Abbreviated New Drug Submission - Prior Approval Supplement (SANDS-PAS)',
    applicableTypes: ['SANDS'],
  },
  // Notifiable Changes
  {
    code: 'NC',
    label: 'Notifiable Change (NC)',
    applicableTypes: ['NC'],
  },
  {
    code: 'NC-END',
    label: 'Notifiable Change — End of Study (NC)',
    applicableTypes: ['NC'],
  },
  // Drug Identification Number Applications
  {
    code: 'DIN-A',
    label: 'Drug Identification Number Application (DIN-A)',
    applicableTypes: ['DIN-A'],
  },
  // Clinical Trial Applications
  {
    code: 'CTA-N',
    label: 'Clinical Trial Application — New (CTA-N)',
    applicableTypes: ['CTA'],
  },
  {
    code: 'CTA-A',
    label: 'Clinical Trial Application — Amendment (CTA-A)',
    applicableTypes: ['CTA'],
  },
  {
    code: 'CTA-N-PED',
    label: 'Clinical Trial Application — New Paediatric (CTA-N)',
    applicableTypes: ['CTA'],
  },
  // Responses
  {
    code: 'RESPONSE-NON',
    label: 'Response to a Notice of Non-Compliance (NON)',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS'],
  },
  {
    code: 'RESPONSE-NON-W',
    label: 'Response to a Notice of Non-Compliance — Withdrawal (NON-W)',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS'],
  },
  {
    code: 'RESPONSE-CIR',
    label: 'Response to a Clarification Information Request (CIR)',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS', 'NC', 'DIN-A', 'CTA'],
  },
  {
    code: 'RESPONSE-SDN',
    label: 'Response to a Screening Deficiency Notice (SDN)',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS', 'NC', 'DIN-A', 'CTA'],
  },
  {
    code: 'RESPONSE-DIR',
    label: 'Response to a Deficiency Information Request (DIR)',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS', 'NC', 'DIN-A', 'CTA'],
  },
  // Annual Reports / Periodic Reports
  {
    code: 'ANNUAL-REPORT',
    label: 'Annual Report',
    applicableTypes: ['NDS', 'ANDS'],
  },
  {
    code: 'PSUR',
    label: 'Periodic Safety Update Report (PSUR)',
    applicableTypes: ['NDS', 'ANDS'],
  },
  // Lifecycle / Amendments
  {
    code: 'AMENDMENT',
    label: 'Amendment',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS', 'CTA'],
  },
  {
    code: 'WITHDRAWAL',
    label: 'Withdrawal',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS', 'NC', 'DIN-A', 'CTA'],
  },
  // Resubmissions
  {
    code: 'RESUBMISSION',
    label: 'Resubmission',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS'],
  },
  // Labelling
  {
    code: 'LABELLING-UPDATE',
    label: 'Labelling Update',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS'],
  },
  // Transition sequences
  {
    code: 'TRANSITION',
    label: 'Transition to eCTD from paper/NeeS',
    applicableTypes: ['NDS', 'ANDS'],
  },
  // Initial sequence (Sequence 0 — the first dossier entry)
  {
    code: 'INITIAL',
    label: 'Initial Filing',
    applicableTypes: ['NDS', 'ANDS', 'SNDS', 'SANDS', 'NC', 'DIN-A', 'CTA'],
  },
];

/**
 * Return the subset of HC sequence descriptions that apply to a given
 * HC submission type (e.g. 'NDS', 'ANDS', 'CTA').
 */
export function getHcSequenceDescriptionsForType(
  submissionType: string,
): ReadonlyArray<HcSequenceDescription> {
  return HC_SEQUENCE_DESCRIPTIONS.filter((d) =>
    d.applicableTypes.includes(submissionType),
  );
}
